import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import classNames from 'classnames';
import config from "config";

import css from "./style.scss";
import css2 from '../style.scss';

import {
  selectPrimaryUserId,
  selectCustomerNumber,
  selectMarketingProfile,
  selectProfile,
} from "data/user/selectors";

import {
  getMarketingProfile
} from "data/user/actions";

import {
  getContactIds,
  getAppendJobEstimate
} from "data/contacts/actions";

import {
  selectAssignableGroups,
  selectAppendGroups
} from "data/contacts/selectors";

import {
  selectCampaign,
  selectDirectMailCampaigns,
} from "data/campaigns/selectors";

import {
  DirectMailBallpointOrderStatus,
  DirectMailCampaignOrderRecipientUploadStatus,
} from "data/campaigns/constants";

import {
  createDirectMailCampaign,
  getDirectMailCampaign,
  getDirectMailCampaignByBallpointCampaignId,
  getDirectMailCampaignBallpointOrder,
  createDirectMailCampaignOrder,
  confirmDirectMailCampaignSubmission,
  updateDirectMailCampaign,
  updateDirectMailCampaignBallpointRecipients,
  updateDirectMailCampaignOrder,
  cancelDirectMailCampaignOrder,
  getDirectMailCampaignsByMarketingCampaignId,
  getDirectMailListPreview,
  saveDirectMailCampaignRts,
  getDirectMailCostPreview,
  suppressDirectMailRtsContacts,
  updateDirectMailContactOptOuts,
  loadDirectMailCostPreviewSuccess,
  requestDirectMailBallpointEmbedToken,
} from "data/campaigns/actions";

import { useToast } from "app/components/ToastMessage/ToastManager";
import { ContactAssignmentPopup } from "app/components/GroupAssignment";
import AddToMarketingListModal from "app/components/AddToMarketingListModal";
import SkipTraceModal from "app/components/SkipTraceModal";
import InfoAlert from "app/components/InfoAlert";
import CreateDirectMailHandler from "../Dashboard/DirectMail/CreateDirectMailHandler";
import EditMarketingProfilePopup from "../../AccountNew/modals/EditMarketingProfile";

import IFrameMessageHandler from "components/IFrameMessageHandler";
import CampaginPaymentModal from "../../components/CampaginPaymentModal";

import LoaderModal from "app/components/LoaderModal";

const {
  constants: {
    API: { BALLPOINT_API_BASE_URL, BALLPOINT_IFRAME_URL },
  },
} = config;

const EMBED_TOKEN_FIRST_LOAD_RETRY_MS = 2000;
const EMBED_TOKEN_RATE_LIMITED_RETRY_MS = 60000;
const EMBED_TOKEN_RENEWAL_BACKOFF_MS = [5000, 15000, 45000];
const EMBED_TOKEN_RENEWAL_MAX_BACKOFF_MS = 60000;
const EMBED_TOKEN_MAX_AGE_BEFORE_FIRST_SEND_MS = 2 * 60 * 1000;

const LOAD_ERROR_MESSAGE = "Something went wrong loading Direct Mail. Please try again.";
const JWT_DISABLED_MESSAGE = "Direct Mail is temporarily unavailable. Please try again later.";
const RELOAD_MESSAGE = "Direct Mail needs to be reloaded. Please refresh the page.";

// Guards against duplicate mounts of this component (overlapping route matches can
// currently render it more than once for the same URL) ending up with two instances
// each creating their own IFrameMessageHandler/iframe and independently processing
// every message from Ballpoint. Stopgap: only the first instance to render becomes
// active; any other stays inert for its whole lifetime (it does not retroactively
// activate). A new instance mounted after the active one unmounts can claim the slot
// instead. The real fix is to stop the routing from matching the same URL twice.
let _activeDirectMailInstance = null;

// Every iframe document starts knowing nothing: what it has received so far (message type -> content), whether it was
// navigated yet, and how often it asked for its config.
const newIFrameDocument = () => ({ sent: {}, opened: false, configRequests: 0, lastConfigAnswerAt: -Infinity });

// A second request_config this soon after an answer is not answered again.
const MIN_CONFIG_ANSWER_GAP_MS = 3000;

const DirectMailCampaign = ({
  location,
  history,
  openContactAssignmentPopup,
  openAddToMarketingListModal,
  openSkipTraceModal,
  openInfoAlert,
  EditMarketingProfilePopup,
  closeEditMarketingProfilePopup
}) => {
  const dispatch = useDispatch();
  const showToast = useToast();

  const instanceRef = useRef(null);
  if (instanceRef.current === null) {
    instanceRef.current = {};
  }

  const [isActiveInstance] = useState(() => {
    if (_activeDirectMailInstance === null) {
      _activeDirectMailInstance = instanceRef.current;
      return true;
    }

    return false;
  });

  useEffect(() => {
    return () => {
      if (_activeDirectMailInstance === instanceRef.current) {
        _activeDirectMailInstance = null;
      }
    };
  }, []);

  const iFrameId = "ballpoint-direct-mail";
  const IFRAME_MESSAGE_SOURCE = "ballpoint-mailer";
  const SET_API_CONFIG = "set_api_config";
  const SET_LIST = "set_list";
  const SET_DASHBOARD_FILTER = "set_dashboard_filter";
  const OPEN_DIRECT_MAIL_DASHBOARD = "open_direct_mail_dashboard";
  const SET_SENDER = "set_sender";
  const SET_PREVIEW_RECIPIENT = "set_preview_recipient";
  const OPEN_CREATE_DIRECT_MAIL = "open_create_direct_mail";
  const RECIPIENT_OPT_OUT_CHANGED = "recipient_opt_out_changed";

  const READY = "ready";
  const REQUEST_CONFIG = "request_config";
  const CAMPAIGN_HOME_REQUESTED = "campaign_home_requested";
  const OPEN_CREATE_DIRECT_MAIL_FAILED = "open_create_direct_mail_failed";
  const CREATE_DIRECT_MAIL_REQUESTED = "create_direct_mail_requested";
  const CAMPAIGN_CREATED = "campaign_created";
  const EDIT_LEADS_REQUESTED = "edit_leads_requested";
  const RECIPIENTS_UPDATED = "recipients_updated";
  const SENDER_SETUP_REQUESTED = "sender_setup_requested";
  const CAMPAIGN_SUBMITTED = "campaign_submitted";
  const ORDER_RESCHEDULED = "order_rescheduled";
  const ORDER_CANCELLED = "order_cancelled";
  const DOWNLOAD_CSV = "download_csv";
  const ADD_TO_MARKETING_LIST_REQUESTED = "add_to_marketing_list_requested";
  const PAYMENT_RETRY_REQUESTED = "payment_retry_requested";
  const MAILING_ADDRESS = "MAILING_ADDRESS";
  const PROPERTY_ADDRESS = "PROPERTY_ADDRESS";
  const AUTO_SUPPRESS_NEXT_DROP_CHANGED = "auto_suppress_next_drop_changed";
  const BOTH = "BOTH";


  const primaryUserId = useSelector(selectPrimaryUserId);
  const customerNumber = useSelector(selectCustomerNumber);
  const currentMarketingCampaignRaw = useSelector(selectCampaign);
  const currentMarketingCampaign = useMemo(() => currentMarketingCampaignRaw?.toJS(), [currentMarketingCampaignRaw]);
  const marketingProfileRaw = useSelector(selectMarketingProfile);
  const marketingProfile = useMemo(() => marketingProfileRaw?.toJS(), [marketingProfileRaw]);
  const profileRaw = useSelector(selectProfile);
  const profile = useMemo(() => profileRaw?.toJS(), [profileRaw]);
  const assignableGroupsRaw = useSelector(selectAssignableGroups);
  const assignableGroups = useMemo(() => assignableGroupsRaw?.toJS(), [assignableGroupsRaw]);
  const appendGroupsRaw = useSelector(selectAppendGroups);
  const appendGroups = useMemo(() => appendGroupsRaw?.toJS(), [appendGroupsRaw]);

  const currentMarketingCampaignRef = useRef(null);
  const marketingProfileRef = useRef(null);
  const iFrameMessageHandlerRef = useRef(null);
  const createdCampaignIdRef = useRef(null);
  // Ballpoint repeats campaign_submitted: the same payload when Continue to Payment is pressed
  // again, a new one (new order ids) after the user went back and edited. These remember what is
  // already done: the orders created, and the payload whose recipients were uploaded.
  const createdBallpointOrderIdsRef = useRef(new Set());
  const uploadedSubmissionKeyRef = useRef(null);
  const activeDirectMailCampaignIdRef = useRef(null);


  // Counts the `ready` messages of the iframe (every one is a new iframe document); 0 = the current iframe has not said ready yet.
  const [readyEpoch, setReadyEpoch] = useState(0);
  const isIFrameReady = readyEpoch > 0;

  const iframeDocumentRef = useRef(null);
  if (iframeDocumentRef.current === null) iframeDocumentRef.current = newIFrameDocument();

  // A new iframe document knows nothing yet: forget what the previous one received.
  const startIFrameDocument = () => {
    iframeDocumentRef.current = newIFrameDocument();
  };

  const [iframeKey, setIframeKey] = useState(0);
  const [pendingNewCampaign, setPendingNewCampaign] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const loaderTimeoutRef = useRef(null);

  // Safety net for the CREATE_DIRECT_MAIL_REQUESTED loader: the flow it waits on spans an
  // iframe remount and a 'ready' postMessage the iframe controls, so if the iframe never loads
  // (script/network failure, crash before init) neither the success path nor the
  // OPEN_CREATE_DIRECT_MAIL_FAILED message will ever fire to clear it.
  const CREATE_DIRECT_MAIL_LOADER_TIMEOUT_MS = 20000;

  const startLoader = () => {
    setShowLoader(true);
    clearTimeout(loaderTimeoutRef.current);
    loaderTimeoutRef.current = setTimeout(() => {
      console.error("[Direct Mail] Timed out waiting for the Create Direct Mail page to load");
      setShowLoader(false);
    }, CREATE_DIRECT_MAIL_LOADER_TIMEOUT_MS);
  };

  const stopLoader = () => {
    clearTimeout(loaderTimeoutRef.current);
    setShowLoader(false);
  };

  useEffect(() => () => clearTimeout(loaderTimeoutRef.current), []);

  const primaryUserIdRef = useRef(null);
  primaryUserIdRef.current = primaryUserId;

  // Embed token for the current iframe document. Kept in a ref, never in Redux (redux-logger/DevTools would print it).
  const embedTokenRef = useRef(null);
  const embedTokenRequestIdRef = useRef(0);
  const embedTokenTimerRef = useRef(null);
  const embedTokenInFlightRef = useRef(null);
  const isRenewalPendingRef = useRef(false);
  const [hasTokenForMount, setHasTokenForMount] = useState(false);

  const isJwtDisabled = (error) => error.status === 503 && error.code === "JWT_DISABLED";
  // Auth failures (our 401/403, or Ballpoint rejecting the server key) never succeed on retry.
  const isAuthFailure = (error) => error.status === 401 || error.status === 403 || error.code === "BALLPOINT_AUTH";

  const scheduleEmbedTokenTimer = (fn, ms) => {
    clearTimeout(embedTokenTimerRef.current);
    embedTokenTimerRef.current = setTimeout(fn, ms);
  };

  // Every iframe document gets its own, freshly minted token (Iframe Kit: never one cached from an earlier document).
  const resetEmbedToken = () => {
    embedTokenRequestIdRef.current += 1;
    clearTimeout(embedTokenTimerRef.current);
    embedTokenInFlightRef.current = null;
    embedTokenRef.current = null;
    isRenewalPendingRef.current = false;
    setHasTokenForMount(false);
  };

  // Starts a token request without awaiting it, reusing one already in flight for this document;
  // a result that arrives after a new iframe document started is dropped.
  const startEmbedTokenRequest = (onToken, onError) => {
    const requestId = embedTokenRequestIdRef.current;

    if (embedTokenInFlightRef.current?.requestId !== requestId) {
      const promise = requestDirectMailBallpointEmbedToken().finally(() => {
        if (embedTokenInFlightRef.current?.promise === promise) embedTokenInFlightRef.current = null;
      });
      embedTokenInFlightRef.current = { requestId, promise };
    }

    embedTokenInFlightRef.current.promise
      .then(({ token, expiresIn }) => {
        if (requestId !== embedTokenRequestIdRef.current) return;
        const issuedAt = Date.now();
        embedTokenRef.current = { token, issuedAt, expiresAt: issuedAt + (expiresIn || 3600) * 1000 };
        onToken();
      })
      .catch((error) => {
        if (requestId !== embedTokenRequestIdRef.current) return;
        console.error("[Direct Mail] Embed token request failed", error.status, error.code);
        onError(error);
      });
  };

  const apiConfigPayload = () => ({
    apiBaseUrl: BALLPOINT_API_BASE_URL,
    apiToken: embedTokenRef.current.token,
    tenantKey: String(primaryUserIdRef.current),
  });

  // Renewal answer: only set_api_config, recorded in the iframe document so the init effect never re-sends it.
  const sendApiConfig = () => {
    const payload = apiConfigPayload();
    if (iFrameMessageHandlerRef.current?.sendMessage(SET_API_CONFIG, payload) !== false) {
      iframeDocumentRef.current.sent[SET_API_CONFIG] = JSON.stringify(payload);
    }
  };

  const requestFirstEmbedToken = (isRetry = false) => {
    clearTimeout(embedTokenTimerRef.current);
    isRenewalPendingRef.current = false;

    startEmbedTokenRequest(
      () => setHasTokenForMount(true),
      (error) => {
        if (isJwtDisabled(error)) {
          showToast(JWT_DISABLED_MESSAGE, "error");
          return;
        }

        if (isAuthFailure(error)) {
          showToast(LOAD_ERROR_MESSAGE, "error");
          return;
        }

        showToast(LOAD_ERROR_MESSAGE, "error", {
          label: "Try again",
          onClick: () => !embedTokenRef.current && requestFirstEmbedToken(true),
        });

        if (!isRetry) {
          const retryMs = error.status === 429 ? EMBED_TOKEN_RATE_LIMITED_RETRY_MS : EMBED_TOKEN_FIRST_LOAD_RETRY_MS;
          scheduleEmbedTokenTimer(() => requestFirstEmbedToken(true), retryMs);
        }
      }
    );
  };

  // The current token keeps working until expiry, so the reload message waits until then.
  const scheduleReloadMessage = () => {
    const remainingMs = (embedTokenRef.current?.expiresAt ?? 0) - Date.now();
    scheduleEmbedTokenTimer(() => showToast(RELOAD_MESSAGE, "error"), Math.max(remainingMs, 0));
  };

  // Pending from the first renewal attempt until one succeeds (or a new iframe document starts), including
  // after a terminal failure, so request_config on tab focus can't restart the backoff at attempt 0.
  const renewEmbedToken = (attempt = 0) => {
    isRenewalPendingRef.current = true;
    clearTimeout(embedTokenTimerRef.current);

    startEmbedTokenRequest(() => {
      isRenewalPendingRef.current = false;
      sendApiConfig();
    }, (error) => {
      if (isJwtDisabled(error)) {
        showToast(JWT_DISABLED_MESSAGE, "error");
        scheduleReloadMessage();
        return;
      }

      if (isAuthFailure(error)) {
        showToast(LOAD_ERROR_MESSAGE, "error");
        scheduleReloadMessage();
        return;
      }

      const remainingMs = (embedTokenRef.current?.expiresAt ?? 0) - Date.now();
      if (remainingMs <= 0) {
        showToast(RELOAD_MESSAGE, "error");
        return;
      }

      const backoffMs = error.status === 429
        ? EMBED_TOKEN_RATE_LIMITED_RETRY_MS
        : EMBED_TOKEN_RENEWAL_BACKOFF_MS[attempt] ?? EMBED_TOKEN_RENEWAL_MAX_BACKOFF_MS;
      scheduleEmbedTokenTimer(() => renewEmbedToken(attempt + 1), Math.min(backoffMs, remainingMs));
    });
  };

  useEffect(() => () => {
    embedTokenRequestIdRef.current += 1;
    clearTimeout(embedTokenTimerRef.current);
  }, []);

  const directMailCampaigns = useSelector(selectDirectMailCampaigns);

  // True when landing on the create-campaign iframe flow: either navigated here with
  // `isNewCampaign` state, or a new campaign was just created via CREATE_DIRECT_MAIL_REQUESTED
  // (pendingNewCampaignId set below).
  const openCreateCampaignIFramePage = !!location?.state?.isNewCampaign || !!pendingNewCampaign;

  const campaignForCreateCampaignPage = location?.state?.newCampaign || pendingNewCampaign;

  const isSenderSetupPendingRef = useRef(false);

  useEffect(() => {
    if (!isActiveInstance) return;

    iFrameMessageHandlerRef.current = new IFrameMessageHandler(
      iFrameId, BALLPOINT_IFRAME_URL, IFRAME_MESSAGE_SOURCE, { source: 'propstream', version: 1 }, showToast
    );
    return () => {
      startIFrameDocument(); // whatever is still loading for this iframe must not be posted into a later one
      iFrameMessageHandlerRef.current?.destroy();
    };
  }, [isActiveInstance]);

  // Re-resolves the handler's iFrame element reference once the new <iframe> (remounted
  // via the `key` change below) has actually landed in the DOM.
  useEffect(() => {
    iFrameMessageHandlerRef.current?.reset();
  }, [iframeKey]);

  useEffect(() => {
    if (!isActiveInstance) return;

    if (primaryUserId) {
      dispatch(getMarketingProfile(primaryUserId));
    }
  }, [primaryUserId, dispatch, isActiveInstance]);

  useEffect(() => {
    if (!isActiveInstance || !currentMarketingCampaign?.id) return;

    currentMarketingCampaignRef.current = currentMarketingCampaign;

    dispatch(getDirectMailCampaignsByMarketingCampaignId(currentMarketingCampaign.id)).catch(() => {
      showToast("Something went wrong loading your campaigns. Please try again.", "error");
    });
  }, [currentMarketingCampaign?.id, isActiveInstance]);

  useEffect(() => {
    if (!marketingProfile || Object.keys(marketingProfile).length === 0) return;

    marketingProfileRef.current = marketingProfile;
  }, [marketingProfile]);

  /**
   * Updates a Direct Mail campaign's Ballpoint campaign association, name, and type.
   *
   * Called when the Ballpoint iFrame fires a `campaign_created` or `edit_leads_requested` event.
   * Dispatches `updateDirectMailCampaign` with:
   *   - the Ballpoint campaign ID to associate with the local campaign
   *   - the campaign name from the iFrame message (via the `listName` field)
   *   - the campaign type from the iFrame message, uppercased
   *
   * @param {string} campaignId - The local Direct Mail campaign ID (corresponds to `listId` in the iFrame message).
   * @param {string} ballpointCampaignId - The Ballpoint-side campaign ID to link to the local campaign.
   * @param {string|null} campaignName - The campaign name from the iFrame message (passed via the `listName` field).
   * @param {string} campaignType - The campaign type from the iFrame message; uppercased before persisting.
   */
  const handleUpdateDirectMailCampaign = async (campaignId, ballpointCampaignId, campaignName, campaignType) => {
    return await dispatch(updateDirectMailCampaign(campaignId, {
      ballpointCampaignId: ballpointCampaignId,
      name: campaignName,
      campaignType: campaignType.toUpperCase(),
    }));
  }

  /**
   * Sends `set_preview_recipient` to the Ballpoint iFrame so eligible handwritten-postcard
   * proofs resolve merge tags to real recipient data instead of showing the raw alias.
   * Must be sent after an accepted `set_list` for the same `listId` (IFRAME_KIT.md §5).
   *
   * `placeHolders` is passed through wholesale (not trimmed to PropertyStreet/PropertyCity) so
   * this preview payload carries the same Owner/Property merge fields as the real recipient
   * upload (API_KIT.md §6n) - the iframe only reads PropertyStreet/PropertyCity/PropertyValue
   * for this message, but the rest are harmless ("unrelated recipient fields are ignored").
   *
   * @param {string} listId - Must match the active singular `set_list.listId`.
   * @param {Object|null} previewRecipient - The `previewRecipient` from the list-preview
   *   endpoint response, or `null` when the group has no valid mailing-address recipient
   *   (sent through as `recipient: null` to explicitly clear the iFrame's proof).
   */
  const sendPreviewRecipient = (listId, previewRecipient) => {
    iFrameMessageHandlerRef.current.sendMessage(SET_PREVIEW_RECIPIENT, {
      tenantKey: String(primaryUserId),
      listId,
      recipient: previewRecipient ? {
        first_name: previewRecipient.first_name,
        last_name: previewRecipient.last_name,
        address: previewRecipient.address,
        city: previewRecipient.city,
        state: previewRecipient.state,
        zip: previewRecipient.zip,
        placeHolders: previewRecipient.placeHolders,
      } : null,
    });
  };

  /**
   * Opens the contact assignment popup for the given Direct Mail campaign, allowing the user
   * to edit the campaign's lead list.
   *
   * Fetches the current contact IDs for the campaign's contact group, then opens the
   * ContactAssignmentPopup. On save:
   *   1. Dispatches `updateDirectMailCampaign` with the updated contact IDs.
   *   2. Fetches the updated piece counts + preview recipient for the campaign's (possibly
   *      new) contact group.
   *   3. Sends a `set_list` message to the Ballpoint iFrame with the campaign's new total
   *      lead count and piece counts, so the iFrame's UI reflects the edited list, then
   *      sends `set_preview_recipient` so the handwritten-postcard proof reflects it too.
   *
   * @param {Object} directMailCampaign - The Direct Mail campaign whose leads should be edited.
   * @param {number} directMailCampaign.id - The campaign ID used for the update call.
   * @param {number} directMailCampaign.contactGroupId - The contact group ID used to fetch current contacts.
   */
  const handleEditLeadsRequested = async (directMailCampaign) => {
    dispatch(getContactIds(
      "C" + directMailCampaign.contactGroupId,
      ({ response }) => {
        openContactAssignmentPopup({
          ids: response,
          onSave: async (updatedContactIds) => {
            try {
              const updatedCampaign = await dispatch(
                updateDirectMailCampaign(directMailCampaign.id, {
                  contactIds: updatedContactIds,
                })
              );

              const { pieceCounts, previewRecipient } = await dispatch(getDirectMailListPreview(updatedCampaign.contactGroupId));

              iFrameMessageHandlerRef.current.sendMessage(SET_LIST, {
                listId: String(updatedCampaign.id),
                count: updatedCampaign.totalLeadsCount,
                piece_counts: pieceCounts,
              });

              sendPreviewRecipient(String(updatedCampaign.id), previewRecipient);
            } catch {
              showToast("Something went wrong and the lead changes were not saved. Please try again.", "error");
            }
          },
        });
      },
      () => {
        showToast("Something went wrong loading the campaign's leads. Please try again.", "error");
      },
      true
    ));
  };

  /**
   * Opens the contact assignment popup for a Direct Mail campaign whose `edit_leads_requested`
   * event came from a post-creation scope (`single_send`, `multi_month_campaign`, `ab_split` —
   * i.e. a campaign card in My Campaigns, as opposed to `creation_flow`, which is handled by
   * `handleEditLeadsRequested` above and never reaches this function).
   *
   * Fetches the current contact IDs for the campaign's contact group (the "before" snapshot),
   * then opens the same ContactAssignmentPopup used by the creation flow. On save:
   *   1. Dispatches `updateDirectMailCampaign` with the updated ("after") contact IDs.
   *   2. Diffs the before/after contact ID lists into `contactIdsAdded` / `contactIdsRemoved`
   *      and dispatches `updateDirectMailCampaignBallpointRecipients` so the linked Ballpoint
   *      campaign's recipients stay in sync. Our backend owns translating this flat delta into
   *      whatever Ballpoint-side calls are needed (campaign-level delta, per-order, A/B-split
   *      allocation) — the frontend never calls Ballpoint's API directly.
   *   3. Only if that sync succeeds, sends `recipients_updated` back to the iFrame so it
   *      refreshes the My Campaigns card for the affected orders, using the `ballpointOrderId`
   *      of each order in the sync response's `dropsAffected`. If the sync fails, the error
   *      is swallowed — it must not surface a blocking error to the user — and
   *      `recipients_updated` is skipped for this save. (TODO: currently not logged or toasted.)
   *
   * @param {Object} directMailCampaign - The Direct Mail campaign whose leads should be edited.
   * @param {number} directMailCampaign.id - The campaign ID used for the update call.
   * @param {number} directMailCampaign.contactGroupId - The contact group ID used to fetch current contacts.
   * @param {string} directMailCampaign.ballpointCampaignId - The linked Ballpoint campaign ID.
   * @param {string|null} iframeCampaignId - Iframe-local campaign handle (`message.campaignId`), echoed back on `recipients_updated`.
   * @param {string} campaignDeltaEndpoint - The Ballpoint-side endpoint hint (`message.campaignDeltaEndpoint`) forwarded to `updateDirectMailCampaignBallpointRecipients` so the backend knows which delta call to make.
   */
  const handlePostCreationEditLeadsRequested = async (directMailCampaign, iframeCampaignId, campaignDeltaEndpoint) => {
    dispatch(getContactIds(
      "C" + directMailCampaign.contactGroupId,
      ({ response }) => {
        const previousContactIds = response;

        openContactAssignmentPopup({
          ids: previousContactIds,
          onSave: async (updatedContactIds) => {
            const contactIdsAdded = updatedContactIds.filter((id) => !previousContactIds.includes(id));
            const contactIdsRemoved = previousContactIds.filter((id) => !updatedContactIds.includes(id));

            try {
              const { dropsAffected } = await dispatch(
                updateDirectMailCampaignBallpointRecipients(
                  directMailCampaign.ballpointCampaignId,
                  contactIdsAdded,
                  contactIdsRemoved,
                  campaignDeltaEndpoint,
                )
              );

              const affectedBallpointOrderIds = (dropsAffected || []).map((drop) => drop.ballpointOrderId);

              if (affectedBallpointOrderIds.length == 0) return;

              await dispatch(
                updateDirectMailCampaign(directMailCampaign.id, {
                  contactIds: updatedContactIds,
                })
              );

              iFrameMessageHandlerRef.current.sendMessage(RECIPIENTS_UPDATED, {
                tenantKey: String(primaryUserId),
                campaignId: iframeCampaignId,
                ballpointCampaignId: directMailCampaign.ballpointCampaignId,
                updatedBallpointOrderIds: affectedBallpointOrderIds,
              });
            } catch {
              showToast("Something went wrong and the lead changes were not saved. Please try again.", "error");
            }
          },
        });
      },
      () => {
        showToast("Something went wrong loading the campaign's leads. Please try again.", "error");
      },
      true
    ));
  };

  /**
   * Kicks off a Skip Trace append job for the given contact ids and opens the Skip Trace
   * modal on success, or an "already in progress" alert if one is already running for
   * this account. Mirrors the pattern used for property-originated skip traces (e.g.
   * Search, withProperty), but operates directly on contact ids since the RTS suppression
   * entries are already resolved contacts — no property-to-contact lookup is needed.
   *
   * @param {number[]} contactIds
   */
  const handleSkipTraceContacts = (contactIds) => {
    if (!contactIds.length) return;

    dispatch(getAppendJobEstimate(
      { contactIds, appendMode: 'BOTH', reprocessEnabled: false },
      ({ response }) => {
        if (response?.message?.includes('There is an existing append job')) {
          openInfoAlert({
            title: 'Skip Trace in Progress',
            infoText: 'There is a Skip Trace currently in progress. Please try again once it is complete.',
          });
        } else {
          openSkipTraceModal({
            groups: appendGroups,
            reprocessEnabled: false,
            contactIds,
            selectedProperties: contactIds.length,
          });
        }
      },
      (error) => {
        console.error('Error fetching skip trace estimate:', error);
        showToast("Something went wrong and Skip Trace could not be started. Please try again.", "error");
      }
    ));
  };

  /**
   * Opens the Add to Marketing List modal in response to the Ballpoint iFrame's
   * `add_to_marketing_list_requested` event, letting the user pick an existing Contact Group
   * or create a new one, then assigns the Direct Mail campaign's RTS contacts to
   * that contact group via the Direct Mail RTS endpoint. If the user checks Skip Trace,
   * the same recipients are queued into the skip trace workflow after the save succeeds.
   *
   * @param {string} ballpointCampaignId - The Ballpoint campaign ID from the iFrame message.
   * @param {Array<{contact_id: string, contact_type: string|null}>} [recipients] - RTS
   *   suppression entries the iFrame handed over, per the `add_to_marketing_list_requested`
   *   contract. Only entries carrying a `contact_id` are usable for skip tracing.
   */
  const handleCreateMarketingList = (ballpointCampaignId, recipients = []) => {
    // `recipients = []` only guards a missing field — an explicit `null` from the iFrame
    // would still slip through and crash the `.map()` below later, inside `onSave` (which
    // fires from a user click, long after this function returns, so nothing wraps it).
    recipients = Array.isArray(recipients) ? recipients : [];

    openAddToMarketingListModal({
      title: 'Add to Marketing List',
      checkboxLabel: 'Skip Trace Selected Recipients',
      groups: assignableGroups,
      onSave: (name, onComplete, saveOptions) => {
        // Contact group ids carry a type-prefix letter (e.g. "C1234") — strip it, the RTS endpoint wants the bare number.
        const contactGroupId = saveOptions?.id ? Number(String(saveOptions.id).replace(/\D/g, '')) || null : null;

        dispatch(saveDirectMailCampaignRts(
          name,
          ballpointCampaignId,
          contactGroupId,
          ({ _ }) => {
            onComplete();

            if (saveOptions?.skipTrace) {
              const contactIds = recipients
                .map((recipient) => Number(recipient?.contact_id))
                .filter((contactId) => Number.isFinite(contactId));

              handleSkipTraceContacts(contactIds);
            }
          },
          () => {
            showToast("Something went wrong and the marketing list was not saved. Please try again.", "error");
          }
        ));
      },
    });
  };

  // Ballpoint sends this value in different cases per message ("mailing" in
  // recipient_selection, "MAILING" in recipient_opt_out_changed), so match case-insensitively.
  const getDeliverToValue = (deliverTo) => {
    const normalizedDeliverTo = String(deliverTo ?? "").trim().toLowerCase();

    if (normalizedDeliverTo === "mailing") return MAILING_ADDRESS;
    if (normalizedDeliverTo === "property") return PROPERTY_ADDRESS;
    if (normalizedDeliverTo === "both") return BOTH;
    return null;
  };

  /**
   * Syncs the opt-out state of the recipients the iFrame reports in a single
   * `recipient_opt_out_changed` event.
   *
   * The whole batch goes to the backend in ONE request — the endpoint takes an array of
   * entries — rather than one request per recipient. Recipients missing a usable contact id
   * or carrying an address type we can't map are dropped from the batch (and logged) instead
   * of failing the request for the rest; if nothing is usable, no call is made at all.
   *
   * On failure the user is toasted: the iFrame has already flipped the toggle in its own UI,
   * so a silent failure would leave them believing the change was saved when it wasn't.
   *
   * Ballpoint's `contact_type` is the raw address target ("MAILING", "mailing", ...), so it is
   * normalized through `getDeliverToValue` into the address enum value the backend expects.
   *
   * @param {Object} message - The `recipient_opt_out_changed` iFrame message.
   * @param {boolean} message.opted_out - The new opt-out state to apply to every recipient in the batch.
   * @param {Array<{ contact_id: string|number, contact_type: string|null }>} [message.recipients] - Affected recipients.
   */
  const handleRecipientOptOutChanged = async ({ opted_out, recipients = [] }) => {
    // `recipients = []` only guards a missing field — an explicit `null` from the iFrame
    // would still slip through and crash `.reduce` below (same gap fixed in
    // handleCreateMarketingList).
    recipients = Array.isArray(recipients) ? recipients : [];

    const isOptedOut = !!opted_out;

    const contactOptOuts = recipients.reduce((batch, recipient) => {
      const contactId = Number(recipient?.contact_id);
      const addressType = getDeliverToValue(recipient?.contact_type);

      if (!Number.isFinite(contactId) || !addressType) {
        console.warn(
          "[Direct Mail] recipient_opt_out_changed skipped recipient with a missing contact id or unrecognized contact type",
          recipient
        );
        return batch;
      }

      batch.push({ contactId, addressType, isOptedOut });
      return batch;
    }, []);

    if (!contactOptOuts.length) return;

    try {
      await dispatch(updateDirectMailContactOptOuts(contactOptOuts));
    } catch (error) {
      console.error("[Direct Mail] Failed to sync recipient opt-outs", error);

      showToast(
        `Something went wrong and the opt-out ${contactOptOuts.length > 1 ? "changes were" : "change was"} not saved. Please try again.`,
        "error"
      );
    }
  };

  /**
   * Persists a DirectMailCampaignOrder record for each order returned by the Ballpoint iframe
   * after the user completes checkout.
   *
   * Orders are processed sequentially — each order fully completes before the next starts,
   * preventing race conditions. For each order:
   *   1. Fetches the full Ballpoint order (pricing, product, postage details) by ballpointOrderId.
   *   2. Fetches the local Direct Mail campaign linked to that Ballpoint campaign via campaignId.
   *   3. Updates the Direct Mail campaign with recipient selection settings (deliverTo,
   *      removeDuplicateMailingAddresses) if recipientSelection is provided.
   *   4. Creates a DirectMailCampaignOrder joining the iframe order, Ballpoint order, and local
   *      campaign — capturing all details at the moment of submission.
   *
   * After the loop, a single confirm-submission call is made to the backend with the
   * Ballpoint order IDs of all submitted orders, which triggers the upload of the
   * campaign's recipients to Ballpoint for those orders.
   *
   * Both action creators return their fetched data directly (thunk/Promise pattern), so results
   * are used as local variables rather than relying on Redux state, avoiding stale closure issues.
   *
   * Orders created by an earlier attempt of the same submission are skipped (sending an existing
   * order again fails on the unique Ballpoint order id), so a retry only creates the missing ones.
   * Creating the order at index 0 makes the backend replace the campaign's previous orders (a
   * go-back-and-edit submission), so nothing is cleaned up here.
   * A refused creation of the first order (HTTP 400, part of the campaign is already paid) or an
   * order that got no recipients shows the reason and returns null; any other failure returns
   * false; success returns true.
   *
   * @param {Array<{ orderId: string, ballpointOrderId: string, mailDate: string }>} orders - Orders from the iframe CAMPAIGN_SUBMITTED message. Each order includes its own scheduled mail date (ISO 8601).
   * @param {string} ballpointCampaignId - The Ballpoint campaign ID used to look up the linked local Direct Mail campaign.
   * @param {Object|null} recipientSelection - Recipient selection settings from the iframe message;
   *   used to update the campaign's `deliverTo` and `removeDuplicateMailingAddresses` fields before creating orders.
   */
  const handleCampaignSubmitted = async (orders, ballpointCampaignId, directMailCampaign, recipientSelection) => {
    // Update Direct Mail Campaign with recipient selection values
    if (
      recipientSelection &&
      directMailCampaign?.id
    ) {
      try {
        await dispatch(
          updateDirectMailCampaign(directMailCampaign.id, {
            deliverTo: getDeliverToValue(recipientSelection.deliver_to),
            removeDuplicateMailingAddresses:
              recipientSelection.remove_duplicate_addresses,
          })
        );
      } catch {
        // No toast here — the CAMPAIGN_SUBMITTED handler already shows the same
        // message whenever this function returns false, for every path, not just this one.
        return false;
      }
    }

    for (const [index, order] of orders.entries()) {
      if (createdBallpointOrderIdsRef.current.has(order.ballpointOrderId)) continue;

      try {
        const ballpointOrder = await dispatch(
          getDirectMailCampaignBallpointOrder(order.ballpointOrderId)
        );

        await dispatch(
          createDirectMailCampaignOrder({
            directMailCampaignId: directMailCampaign.id,
            contactGroupId: directMailCampaign.contactGroupId,
            orderIndex: index,
            iframeOrderId: order.orderId,
            ballpointOrderId: ballpointOrder.id,
            productType: ballpointOrder.productType,
            postageType: ballpointOrder.postageType,
            envelopeStyle: ballpointOrder.envelopeStyle,
            scheduledMailDate: new Date(order.mailDate).getTime(),
            pieceCount: ballpointOrder.pieceCount,
            unitPriceTcents: order.unit_price_tcents,
            totalPriceTcents: order.total_tcents,
            status: DirectMailBallpointOrderStatus.SUBMITTED,
            recipientsUploadStatus:
              DirectMailCampaignOrderRecipientUploadStatus.PENDING,
            campaignType: directMailCampaign.campaignType
          })
        );

        createdBallpointOrderIdsRef.current.add(order.ballpointOrderId);
      } catch (error) {
        const status = error?.response?.status ?? error?.response?.statusCode;

        // Only the first order can be refused: the backend checks for paid orders only when
        // order 0 is created.
        if (index === 0 && status === 400 && error?.resBody?.userMessage) {
          showToast(error.resBody.userMessage, 'error');
          return null;
        }

        // No toast here — see the comment above the first catch in this function.
        return false;
      }
    }

    // Confirm the submitted orders to the backend so it uploads the
    // campaign's recipients to Ballpoint for those orders. The backend replaces an order's
    // recipients on every call, so repeating this after a failed or partial upload is safe.
    if (orders.length > 0) {
      try {
        const submissionResult = await dispatch(
          confirmDirectMailCampaignSubmission(
            ballpointCampaignId,
            orders.map((order) => order.ballpointOrderId)
          )
        );

        // The upload worked but an order got no recipients (for example all are opted out, or none
        // has a valid address of the selected type), so it cannot be billed. Say so: trying again
        // would give the same result.
        const hasOrderWithoutRecipients = submissionResult.orders.some(
          (order) => order.status === 'SUCCESS' && order.accepted === 0
        );

        if (hasOrderWithoutRecipients) {
          showToast(
            'One of your orders has no recipients to mail to, so it cannot be paid. Please check your list.',
            'error'
          );
          return null;
        }

        return submissionResult.orders.every((order) => order.ready && order.accepted > 0);
      } catch {
        // No toast here — see the comment above the first catch in this function.
        return false;
      }
    }

    return false;
  };

  /**
   * Triggers a CSV file download in the browser using a temporary object URL.
   *
   * Creates a Blob from the provided content, attaches it to a temporary anchor element,
   * appends the anchor to the DOM (required for Firefox compatibility), clicks it to
   * initiate the download, then removes the anchor. The object URL is revoked after a
   * short delay to ensure the browser has had time to start the download before the
   * resource is released.
   *
   * Called when the Ballpoint iFrame fires a `download_csv` event.
   *
   * @param {string} filename  - The name to give the downloaded file (e.g. "leads.csv").
   * @param {string} content   - The raw file content to download.
   * @param {string} mimeType  - The MIME type of the file (e.g. "text/csv").
   */
  const handleCSVDownload = (filename, content, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  // Handling received IFrame postMessage events/messages
  useEffect(() => {
    if (!isActiveInstance) return;

    // A new iframe document gets a freshly minted token. The handlers return immediately: IFrameMessageHandler runs
    // them on a serial queue, so awaiting the token call here would hold up every later iframe message.
    iFrameMessageHandlerRef.current.subscribeToMessage(READY, (_) => {
      startIFrameDocument();
      resetEmbedToken();
      setReadyEpoch((epoch) => epoch + 1);
      requestFirstEmbedToken();
    });

    // The first request_config after `ready` is the handshake: the config is on its way (or already sent), so it needs no
    // answer. A later one (before expiry, or on tab focus) is answered with a freshly minted token in set_api_config only,
    // never the rest of the config or the navigation; it is ignored until the first set_api_config of this document and
    // while a renewal is already pending.
    iFrameMessageHandlerRef.current.subscribeToMessage(REQUEST_CONFIG, (_) => {
      const iframeDocument = iframeDocumentRef.current;
      const now = performance.now();

      iframeDocument.configRequests += 1;
      if (iframeDocument.configRequests === 1) return;
      if (!iframeDocument.sent[SET_API_CONFIG] || isRenewalPendingRef.current) return;
      if (now - iframeDocument.lastConfigAnswerAt < MIN_CONFIG_ANSWER_GAP_MS) return;

      iframeDocument.lastConfigAnswerAt = now;
      renewEmbedToken();
    });

    if (currentMarketingCampaign?.id) {
      iFrameMessageHandlerRef.current.subscribeToMessage(CAMPAIGN_HOME_REQUESTED, (_) => {
        history.push(`/campaign/${currentMarketingCampaign.id}`);
      });
    }

    iFrameMessageHandlerRef.current.subscribeToMessage(OPEN_CREATE_DIRECT_MAIL_FAILED, (_) => {
      // The iframe couldn't open the Create Direct Mail page (e.g. after CREATE_DIRECT_MAIL_REQUESTED
      // remounts it) — the loader started at CREATE_DIRECT_MAIL_REQUESTED would otherwise never clear.
      stopLoader();

      if (openCreateCampaignIFramePage) {
        showToast("Something went wrong opening the Create Campaign page. Please try again.", "error");
      }
    });

    iFrameMessageHandlerRef.current.subscribeToMessage(EDIT_LEADS_REQUESTED, async (message) => {
      try {
        if (message.scope === "creation_flow") {
          const directMailCampaign = await dispatch(
            getDirectMailCampaign(message.listId)
          );

          if (!directMailCampaign) {
            showToast("Something went wrong loading the campaign's leads. Please try again.", "error");
            return;
          }

          await handleEditLeadsRequested(directMailCampaign);
        } else {
          const directMailCampaign = await dispatch(
            getDirectMailCampaignByBallpointCampaignId(message.ballpointCampaignId)
          );

          if (!directMailCampaign) {
            showToast("Something went wrong loading the campaign's leads. Please try again.", "error");
            return;
          }

          await handlePostCreationEditLeadsRequested(directMailCampaign, message.campaignId, message.campaignDeltaEndpoint);
        }
      } catch {
        showToast("Something went wrong loading the campaign's leads. Please try again.", "error");
      }
    });

    // Message for the 'campaign_created' event
    // Updates the Direct Mail Campaign's Ballpoint campaign association, name, and type
    iFrameMessageHandlerRef.current.subscribeToMessage(CAMPAIGN_CREATED, async (message) => {
      try {
        await handleUpdateDirectMailCampaign(message.listId, message.campaignId, message.listName, message.campaignType);

        // Not refreshing the campaign list here: at this point the campaign has no order yet,
        // so the backend would (correctly) treat it as abandoned and omit it — refreshing now
        // would just make the campaign disappear from the list mid-flow. The list is refreshed
        // once the submission actually resolves, in the CAMPAIGN_SUBMITTED handler below.
      } catch {
        showToast("Something went wrong while creating your campaign. Please try again.", "error");
      }
    });

    iFrameMessageHandlerRef.current.subscribeToMessage(
      CAMPAIGN_SUBMITTED,
      async (message) => {
        setShowPaymentModal(true);
        setIsPaymentLoading(true);

        try {
          // costPreview is only ever set on success and never cleared otherwise, so a stale
          // preview from an earlier, different campaign could otherwise still look valid to
          // CampaginPaymentModal and let checkout proceed against the wrong campaign if this
          // attempt fails before its own getDirectMailCostPreview call below is ever reached.
          dispatch(loadDirectMailCostPreviewSuccess(null));

          const directMailCampaign = await dispatch(
            getDirectMailCampaignByBallpointCampaignId(message.campaignId)
          );

          // The same payload again (Continue to Payment pressed again after its recipients were
          // uploaded) has nothing left to create or upload: only the cost preview below is read
          // again. A new payload (the user went back and edited) runs in full.
          const submissionKey = [
            message.campaignId,
            (message.orders || []).map((order) => order.ballpointOrderId).join(','),
            JSON.stringify(message.recipient_selection),
          ].join('|');

          const isSubmissionSuccessful =
            uploadedSubmissionKeyRef.current === submissionKey ||
            (await handleCampaignSubmitted(
              message.orders,
              message.campaignId,
              directMailCampaign,
              message.recipient_selection
            ));

          if (!isSubmissionSuccessful) {
            // null: the reason (creation refused, or an order without recipients) was already shown.
            if (isSubmissionSuccessful === false) {
              showToast(
                'Something went wrong while submitting your order. Please try again.',
                'error'
              );
            }
            setShowPaymentModal(false);
            return;
          }

          uploadedSubmissionKeyRef.current = submissionKey;
          activeDirectMailCampaignIdRef.current = directMailCampaign.id;

          await dispatch(
            getDirectMailCostPreview(directMailCampaign.id)
          );
        } catch {
          showToast("Something went wrong while submitting your order. Please try again.", "error");
          setShowPaymentModal(false);
        } finally {
          setIsPaymentLoading(false);

          // Refresh the campaign list either way: on success it now reflects the newly created
          // order(s); on failure (e.g. the order was never persisted locally) it reflects that
          // the campaign didn't advance, rather than leaving a stale pre-submission snapshot.
          dispatch(getDirectMailCampaignsByMarketingCampaignId(currentMarketingCampaignRef.current.id)).catch(() => {
            showToast("Something went wrong loading your campaigns. Please try again.", "error");
          });
        }
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(
      AUTO_SUPPRESS_NEXT_DROP_CHANGED,
      async (message) => {
        try {
          const response = await dispatch(
            suppressDirectMailRtsContacts(
              message.ballpointCampaignId,
              message.enabled
            )
          );

          const updatedBallpointOrderIds =
            (response.dropsAffected || []).map(
              drop => drop.ballpointOrderId
            );

          iFrameMessageHandlerRef.current.sendMessage(RECIPIENTS_UPDATED, {
            tenantKey: String(primaryUserId),
            campaignId: message.campaignId,
            ballpointCampaignId: message.ballpointCampaignId,
            updatedBallpointOrderIds,
          });
        } catch {
          showToast("Something went wrong and the setting change was not saved. Please try again.", "error");
        }
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(
      PAYMENT_RETRY_REQUESTED,
      async (_) => {
        // Only a submission handled in this session has a campaign to read the preview of.
        if (!activeDirectMailCampaignIdRef.current) {
          showToast(
            "Something went wrong and we couldn't load your order. Please try again.",
            'error'
          );
          return;
        }

        setShowPaymentModal(true);
        setIsPaymentLoading(true);

        try {
          // Read the cost preview again, so a partly paid A/B shows only the unpaid orders.
          dispatch(loadDirectMailCostPreviewSuccess(null));

          await dispatch(getDirectMailCostPreview(activeDirectMailCampaignIdRef.current));
        } catch {
          showToast(
            "Something went wrong and we couldn't load your order. Please try again.",
            'error'
          );
          setShowPaymentModal(false);
        } finally {
          setIsPaymentLoading(false);
        }
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(ORDER_RESCHEDULED, async (message) => {
      try {
        // The 'updateDirectMailCampaignOrder' action method accepts 'orderId' or 'ballpointOrderId'.
        // In the current logic, we are calling it with 'ballpointOrderId', retrieved from the iframe message.
        await dispatch(updateDirectMailCampaignOrder(null, message.orderId, { scheduledMailDate: new Date(message.newMailDate).getTime() }));
      } catch {
        showToast("Something went wrong and the order was not rescheduled. Please try again.", "error");
      }
    });

    // Ballpoint has already cancelled the order server-side by the time this fires (the user
    // cancelled from inside the iframe). As with ORDER_RESCHEDULED above, 'orderId' and
    // 'campaignId' here are the Ballpoint-side order/campaign ids.
    iFrameMessageHandlerRef.current.subscribeToMessage(ORDER_CANCELLED, async (message) => {
      try {
        await dispatch(cancelDirectMailCampaignOrder(message.orderId));
      } catch {
        showToast("Something went wrong and the order cancellation was not saved. Please try again.", "error");
      }
    });

    iFrameMessageHandlerRef.current.subscribeToMessage(DOWNLOAD_CSV, (message) => {
      handleCSVDownload(message.filename, message.content, message.mime_type);
    });

    iFrameMessageHandlerRef.current.subscribeToMessage(
      SENDER_SETUP_REQUESTED,
      () => {
        isSenderSetupPendingRef.current = true;
        EditMarketingProfilePopup({
          // Called right after the saved profile is in the store, before the popup closes: the iframe is answered with
          // set_sender even if the saved content did not change (it may be waiting for it).
          onSuccess: () => {
            delete iframeDocumentRef.current.sent[SET_SENDER];
          },
          onClose: () => {
            isSenderSetupPendingRef.current = false;
            closeEditMarketingProfilePopup();
          },
          onError: (error) => {
            isSenderSetupPendingRef.current = false;
            console.error("Error while updating the Marketing Profile: " + error);
            showToast("Something went wrong updating your marketing profile. Please try again.", "error");
            closeEditMarketingProfilePopup();
          },
        });
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(
      CREATE_DIRECT_MAIL_REQUESTED,
      async () => {
        try {
          startLoader();

          const handler = new CreateDirectMailHandler(
            primaryUserId,
            currentMarketingCampaignRef.current,
            marketingProfile,
            (payload) => dispatch(createDirectMailCampaign(payload, true)),
            (id) => dispatch(getMarketingProfile(id))
          );

          const newCampaign = await handler.run();

          // Clear the deep-link state now that `pendingNewCampaign` is the source of truth —
          // otherwise `location.state.isNewCampaign`/`newCampaign` (see openCreateCampaignIFramePage
          // and campaignForCreateCampaignPage above) would keep taking precedence over the campaign
          // just created here.
          if (location.state?.isNewCampaign || location.state?.newCampaign) {
            history.replace({ ...location, state: undefined });
          }

          setPendingNewCampaign(newCampaign);

          startIFrameDocument();
          resetEmbedToken();
          setReadyEpoch(0);

          setIframeKey(prev => prev + 1);
        } catch (e) {
          console.error(e);
          stopLoader();
          showToast("Something went wrong opening the Create Campaign page. Please try again.", "error");
        }
      }
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(
      RECIPIENT_OPT_OUT_CHANGED,
      // Must return the promise, not just call it — IFrameMessageHandler's serial queue
      // only catches a handler's errors if the handler's return value is chained into it.
      (message) => handleRecipientOptOutChanged(message)
    );

    iFrameMessageHandlerRef.current.subscribeToMessage(ADD_TO_MARKETING_LIST_REQUESTED, (message) => {
      handleCreateMarketingList(message.ballpointCampaignId, message.recipients);
    });
  }, [currentMarketingCampaign, marketingProfile, assignableGroups, appendGroups, isActiveInstance]);

  const convertMarketingProfileToSenderSnapshotJson = (marketingProfile) => {
    return {
      fullName: marketingProfile.businessFullName,
      address: marketingProfile.businessFullStreetAddress ?? [marketingProfile.businessAddressLine1, marketingProfile.businessAddressLine2].filter(Boolean).join(", "),
      city: marketingProfile.businessCity,
      state: marketingProfile.businessState,
      zip: String(marketingProfile.businessZipCode),
      phone: marketingProfile.businessPhone,
      website: marketingProfile.businessWebsite,
      logo: marketingProfile.marketingProfilePicture,
    };
  }

  // Fetches piece counts + preview recipient for the given campaign, seeds the iframe with its
  // list data via SET_LIST and its handwritten-postcard proof via SET_PREVIEW_RECIPIENT, then
  // triggers OPEN_CREATE_DIRECT_MAIL. Called only when an external access point pre-set
  // campaignIdForCreateCampaignPage before the iframe finished initializing.
  const openCreateCampaignFlow = async (campaign) => {
    createdCampaignIdRef.current = campaign.id;
    const iframeDocument = iframeDocumentRef.current;

    const { pieceCounts, previewRecipient } = await dispatch(getDirectMailListPreview(campaign.contactGroupId));

    // The iframe document was replaced while we were loading: this list must not be sent into the new one.
    if (iframeDocumentRef.current !== iframeDocument) return;

    iFrameMessageHandlerRef.current.sendMessage(SET_LIST, {
      count: campaign.totalLeadsCount,
      name: campaign.name,
      listId: String(campaign.id),
      externalUserId: String(primaryUserId),
      externalUserIsAccountOwner: profile.type === 'FULL',
      piece_counts: pieceCounts,
      // Ballpoint requires the key to be omitted entirely (not null/empty) when unavailable,
      // and only honors it on the first accepted singular set_list — see IFRAME_KIT.md.
      ...(customerNumber != null ? { customerNumber: String(customerNumber) } : {}),
    });

    sendPreviewRecipient(String(campaign.id), previewRecipient);

    iFrameMessageHandlerRef.current.sendMessage(OPEN_CREATE_DIRECT_MAIL);
  };

  // Initializing the IFrame Mailer: sends the setup to the CURRENT iframe document. A message is sent once and afterwards
  // only when its content changed, so a store reload that brings the same data (new objects, same content) repeats nothing.
  useEffect(() => {
    if (!isIFrameReady || !hasTokenForMount || !directMailCampaigns
      || !primaryUserId || (!marketingProfile || Object.keys(marketingProfile).length === 0)
      || !profile?.type) return;

    const iframeDocument = iframeDocumentRef.current;
    const sendIfChanged = (type, payload, key = JSON.stringify(payload)) => {
      if (iframeDocument.sent[type] === key) return;
      if (iFrameMessageHandlerRef.current.sendMessage(type, payload) !== false) iframeDocument.sent[type] = key;
    };

    const listIds = directMailCampaigns.length ? directMailCampaigns.map((campaign) => String(campaign.id)) : [];

    // The iframe schedules renewal from when it receives the token: a token minted on `ready` but held back by the
    // checks above for too long is replaced before its first send.
    if (!iframeDocument.sent[SET_API_CONFIG]
      && Date.now() - embedTokenRef.current.issuedAt > EMBED_TOKEN_MAX_AGE_BEFORE_FIRST_SEND_MS) {
      setHasTokenForMount(false);
      embedTokenRef.current = null;
      requestFirstEmbedToken();
      return;
    }

    sendIfChanged(SET_API_CONFIG, apiConfigPayload());

    sendIfChanged(SET_SENDER, {
      ...convertMarketingProfileToSenderSnapshotJson(marketingProfile),
      externalUserIsAccountOwner: profile.type === 'FULL',
    });

    sendIfChanged(SET_DASHBOARD_FILTER, { listIds }, [...listIds].sort().join(","));

    // Navigation is not config: once per iframe document, so a data reload (or the marketing campaign catching up with the
    // URL) only updates the config and never moves the user.
    if (iframeDocument.opened) return;
    iframeDocument.opened = true;

    // Deep-link path: user arrived from an access point that pre-selected a campaign.
    // Seed the iframe with that specific list and open the create-campaign flow directly,
    // skipping the dashboard view. A campaign whose create flow was already opened is not opened again: a reloaded iframe
    // goes to the dashboard instead, because createdCampaignIdRef belongs to the campaign, not to the iframe document.
    if (openCreateCampaignIFramePage && campaignForCreateCampaignPage
      && campaignForCreateCampaignPage.id !== createdCampaignIdRef.current) {
      openCreateCampaignFlow(campaignForCreateCampaignPage)
        .catch(() => {
          showToast("Something went wrong opening the Create Campaign page. Please try again.", "error");
        })
        .finally(() => stopLoader());
    }
    else {
      iFrameMessageHandlerRef.current.sendMessage(OPEN_DIRECT_MAIL_DASHBOARD);
      setPendingNewCampaign(null);
    }
  }, [readyEpoch, hasTokenForMount, directMailCampaigns, primaryUserId, marketingProfile, profile]);

  useEffect(() => {
    if (
      isIFrameReady &&
      isSenderSetupPendingRef.current &&
      marketingProfile &&
      Object.keys(marketingProfile).length > 0
    ) {
      isSenderSetupPendingRef.current = false;

      closeEditMarketingProfilePopup();
    }
  }, [marketingProfile, isIFrameReady]);

  if (!isActiveInstance) return null;

  return (
    <>
      <div className={css.container}>
        <div className={classNames(css2.header, css.header)}>
          <div className={css2.left}>
            <div className={css.title}>
              <span title={"Direct Mail"} className={css.campaignName}>{"Direct Mail"}</span>
            </div>
          </div>
        </div>
        <div className={classNames(css2.content, css.body)}>
          <iframe
            key={iframeKey}
            id={iFrameId}
            className={classNames(css.iFrame, { [css.iFrameHidden]: showLoader })}
            src={`${BALLPOINT_IFRAME_URL}/index.html?debug=1&${!openCreateCampaignIFramePage ? `externalUserId=${primaryUserId}` : ''}`}
            sandbox={"allow-scripts allow-same-origin allow-forms allow-downloads"}
          />
        </div>
      </div>
      <CampaginPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        iFrameMessageHandlerRef={iFrameMessageHandlerRef}
        isLoading={isPaymentLoading}
        onPaymentSuccess={() => {
          // The backend leaves a campaign out of the list while its orders are unpaid (SUBMITTED); the charge is what
          // makes them paid. Refresh the list now so the paid campaign reaches the iframe's dashboard filter.
          dispatch(getDirectMailCampaignsByMarketingCampaignId(currentMarketingCampaignRef.current.id)).catch(() => {
            showToast("Something went wrong loading your campaigns. Please try again.", "error");
          });
        }}
      />

      <LoaderModal isOpen={showLoader} />
    </>
  );
};

export default withRouter(
  connect(null, {
    openContactAssignmentPopup: ContactAssignmentPopup.open,
    EditMarketingProfilePopup: EditMarketingProfilePopup.open,
    closeEditMarketingProfilePopup: EditMarketingProfilePopup.close,
    openAddToMarketingListModal: AddToMarketingListModal.open,
    openSkipTraceModal: SkipTraceModal.open,
    openInfoAlert: InfoAlert.open,
  })(DirectMailCampaign)
);
